import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Shield, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast({ title: 'Missing reset token', description: 'Open the link from your email again.', variant: 'destructive' });
      return;
    }
    if (password.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
      return;
    }
    if (password !== confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/auth/reset-password`, { token, newPassword: password });
      setDone(true);
      window.history.replaceState({}, document.title, '/admin/reset');
      toast({ title: 'Password reset successful' });
    } catch (err) {
      toast({
        title: 'Could not reset password',
        description: err.response?.data?.detail || 'This reset link is invalid or has expired.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="admin-reset-page">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] p-4 rounded-full">
              <Shield className="w-12 h-12 text-gray-900" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Reset Password</CardTitle>
          <p className="text-gray-600 mt-2">Choose a new admin password</p>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
              <p className="text-gray-700">Your password has been reset. You can now sign in.</p>
              <Button
                className="w-full bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold"
                onClick={() => navigate('/admin/login')}
                data-testid="reset-goto-login"
              >
                Go to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" autoComplete="new-password">
              <div className="space-y-2">
                <Label htmlFor="new" className="flex items-center gap-2"><Lock className="w-4 h-4" /> New password</Label>
                <Input id="new" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="reset-new" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="flex items-center gap-2"><Lock className="w-4 h-4" /> Confirm new password</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" required data-testid="reset-confirm" />
              </div>
              <Button type="submit" className="w-full bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold" disabled={loading || !token} data-testid="reset-submit">
                {loading ? 'Saving…' : 'Reset password'}
              </Button>
              {!token && <p className="text-sm text-red-600 text-center">This link is missing its token. Open the reset link from your email again.</p>}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminResetPassword;
